import { Activity, BarChart3, Clock, TrendingUp, Users } from 'lucide-react';
import React from 'react';
import { EventLogClinicDashboard } from '../../services/eventLog.service';
import LineChart from './charts/LineChart';
import { computeTrend, formatTrend, trendArrow, trendColor } from './utils/trend';

type Props = {
  summary?: EventLogClinicDashboard;
  daily?: EventLogClinicDashboard;
  loading?: boolean;
  filteredUserName?: string;
};

const formatJoined = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';

  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatMinutes = (minutes: number): string => {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;

  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const StatusPill: React.FC<{ status?: string }> = ({ status }) => {
  if (!status) return null;
  const s = status.toLowerCase();
  const color =
    s === 'active'
      ? 'bg-green-100 text-green-700'
      : s === 'canceled' || s === 'terminated' || s === 'expired'
        ? 'bg-red-100 text-red-700'
        : 'bg-gray-100 text-gray-700';

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${color}`}
    >
      {status}
    </span>
  );
};

type KpiProps = {
  label: string;
  value: number;
  previous: number;
  suffix?: string;
  subtitle?: string;
  isInverse?: boolean;
  formatter?: (value: number) => string;
  icon?: React.ReactNode;
  accentColor?: string;
};

const ICON_COLORS: Record<string, string> = {
  'bg-blue-100': 'text-blue-600',
  'bg-emerald-100': 'text-emerald-600',
  'bg-purple-100': 'text-purple-600',
  'bg-rose-100': 'text-rose-600',
};

const Kpi: React.FC<KpiProps> = ({
  label,
  value,
  previous,
  suffix,
  subtitle,
  isInverse,
  formatter,
  icon,
  accentColor,
}) => {
  const trend = computeTrend(value, previous);
  const display = formatter ? formatter(value) : value.toLocaleString();
  const iconColor = accentColor ? ICON_COLORS[accentColor] || 'text-gray-600' : 'text-blue-600';

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-3 mb-2">
        {icon && (
          <div className={`p-2.5 rounded-lg ${accentColor || 'bg-blue-100'} flex-shrink-0`}>
            <div className={iconColor}>{icon}</div>
          </div>
        )}
        <span className="text-2xl font-bold text-gray-900 tabular-nums">
          {display}
          {suffix && <span className="text-base font-normal text-gray-500 ml-1">{suffix}</span>}
        </span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-xs font-medium text-gray-600">
          {label}
          {subtitle && <span className="text-[10px] text-gray-400 ml-1">({subtitle})</span>}
        </span>
        <span
          className={`text-sm font-semibold flex items-center gap-0.5 ${trendColor(trend.direction, isInverse)}`}
        >
          {trendArrow(trend.direction)} {formatTrend(trend, formatter)}
        </span>
      </div>
    </div>
  );
};

const SkeletonKpi: React.FC = () => (
  <div className="rounded-lg border border-gray-200 bg-white p-4">
    <div className="flex items-center gap-3 mb-2">
      <div className="h-11 w-11 rounded-lg skeleton-shimmer" />
      <div className="h-7 w-16 rounded skeleton-shimmer" />
    </div>
    <div className="flex items-center gap-1.5">
      <div className="h-3 w-20 rounded skeleton-shimmer" />
      <div className="h-3 w-10 rounded skeleton-shimmer" />
    </div>
  </div>
);

const SummaryCard: React.FC<Props> = ({ summary, daily, loading, filteredUserName }) => {
  const clinic = summary?.clinic;
  const kpis = summary?.kpis;
  const prev = summary?.previousKpis;
  const dailyData = daily?.daily ?? [];

  return (
    <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50/80 via-white to-gray-50/50 p-5 shadow-sm transition-all duration-300">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2 mb-5">
        <div className="text-xs text-gray-500 flex items-center gap-2">
          <span>
            Plan: <span className="text-gray-700 font-medium">{clinic?.plan || '—'}</span>
          </span>
          <span className="text-gray-300">·</span>
          <StatusPill status={clinic?.status} />
          <span className="text-gray-300">·</span>
          <span>
            Joined:{' '}
            <span className="text-gray-700 font-medium">
              {formatJoined(clinic?.subscriptionStartDate)}
            </span>
          </span>
        </div>
        {filteredUserName && (
          <div className="ml-auto text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 text-primary-700 px-2 py-0.5 font-medium">
              Filtered to {filteredUserName}
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-4 gap-4 mb-6">
        {loading && !summary ? (
          <>
            <SkeletonKpi />
            <SkeletonKpi />
            <SkeletonKpi />
            <SkeletonKpi />
          </>
        ) : (
          <>
            <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-blue-50/40 to-white p-4 hover:shadow-sm transition-all duration-300">
              <Kpi
                label="Events"
                value={kpis?.totalEvents ?? 0}
                previous={prev?.totalEvents ?? 0}
                icon={<BarChart3 className="h-6 w-6" />}
                accentColor="bg-blue-100"
              />
            </div>
            <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-emerald-50/40 to-white p-4 hover:shadow-sm transition-all duration-300">
              <Kpi
                label="Active Users"
                value={kpis?.activeUsers ?? 0}
                previous={prev?.activeUsers ?? 0}
                icon={<Users className="h-6 w-6" />}
                accentColor="bg-emerald-100"
              />
            </div>
            <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-purple-50/40 to-white p-4 hover:shadow-sm transition-all duration-300">
              <Kpi
                label="Time in App"
                value={kpis?.timeInAppMinutes ?? 0}
                previous={prev?.timeInAppMinutes ?? 0}
                formatter={formatMinutes}
                icon={<Clock className="h-6 w-6" />}
                accentColor="bg-purple-100"
                subtitle={
                  (kpis?.daysActive ?? 0) > 0
                    ? `~${formatMinutes(Math.round((kpis?.timeInAppMinutes ?? 0) / kpis!.daysActive))} / day`
                    : undefined
                }
              />
            </div>
            <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-rose-50/40 to-white p-4 hover:shadow-sm transition-all duration-300">
              <Kpi
                label="Days Active"
                value={kpis?.daysActive ?? 0}
                previous={prev?.daysActive ?? 0}
                suffix={`/ ${kpis?.rangeDays ?? 0}`}
                icon={<TrendingUp className="h-6 w-6" />}
                accentColor="bg-rose-100"
              />
            </div>
          </>
        )}
      </div>

      <div className="border-t border-gray-100 pt-4">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="h-4 w-4 text-indigo-500" />
          <span className="text-sm font-medium text-gray-700">Activity Trend</span>
          {daily?.granularity && (
            <span className="text-[10px] text-gray-400 uppercase tracking-wider">
              {daily.granularity}
            </span>
          )}
        </div>
        <div className="rounded-lg bg-gradient-to-br from-indigo-50/40 via-white to-purple-50/30 p-3 -mx-1">
          {loading && !daily ? (
            <div className="h-40 rounded bg-gray-50 animate-pulse" />
          ) : (
            <LineChart data={dailyData} height={160} granularity={daily?.granularity} />
          )}
        </div>
      </div>
    </div>
  );
};

export default SummaryCard;
